import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { adminApi } from "./api";
import { ADMIN_BASE_PATH } from "./admin-path";
import { Badge, Button, ButtonLink, EmptyState, LoadingBlock, PageTitle } from "./ui";

type PayloadRef = {
  source: "audit" | "model_call" | "worker_task";
  part: "input" | "output" | "error" | "request" | "response";
  label: string;
  runId?: string;
  kind?: "event" | "model_call";
  itemId: string;
};

type DiagnosticRecord = {
  id: string;
  stage: string;
  label: string;
  status: string;
  source: "project" | "database" | "audit";
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  slideIndex?: number;
  attemptNo?: number;
  summary?: unknown;
  payloads?: PayloadRef[];
};

type DiagnosticData = {
  task: { id: string; numericId: number; title: string; status: string; generationMode?: string; inputKind: string; createdAt: string; updatedAt: string };
  flow: {
    type: "new_ppt_standard" | "new_ppt_image" | "existing_ppt_image_beautify";
    label: string;
    evidence: Record<string, unknown>;
    warnings: string[];
    expectedStages: Array<{ id: string; label: string; description: string; observed: boolean; recordIds: string[] }>;
  };
  coverage: { level: string; auditRunCount: number; selectedAuditRunId?: string; modelCallCount: number; availableModelPayloads: number; notes: string[] };
  auditRuns: Array<Record<string, unknown>>;
  records: DiagnosticRecord[];
  modelCalls: Array<Record<string, unknown> & { id: string; requestAvailable: boolean; responseAvailable: boolean }>;
  snapshots: { input?: unknown; sourceFacts?: unknown; deck?: unknown };
  summary: { recordCount: number; generationCount: number; renderCount: number; workerTaskCount: number; slideStateCount: number; modelCallCount: number };
};

type ProjectLogExportTask = {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  progressPercent?: number;
  progressText?: string;
  errorMessage?: string;
  downloadUrl?: string;
  fileName?: string;
  archiveBytes?: number;
};

export function AdminProjectDiagnosticsPage() {
  const { taskId = "" } = useParams();
  const [data, setData] = useState<DiagnosticData>();
  const [error, setError] = useState("");
  const [selectedRunId, setSelectedRunId] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [logExport, setLogExport] = useState<ProjectLogExportTask | null>();
  const [exportBusy, setExportBusy] = useState(false);

  const load = useCallback(async (runId = selectedRunId) => {
    setError("");
    try {
      const suffix = runId ? `?runId=${encodeURIComponent(runId)}` : "";
      const result = await adminApi<DiagnosticData>(`/api/admin/ppt-tasks/${encodeURIComponent(taskId)}/diagnostics${suffix}`);
      setData(result);
      setSelectedRunId(String(result.coverage.selectedAuditRunId || ""));
    } catch (caught) {
      setData(undefined);
      setError(caught instanceof Error ? caught.message : "详细数据加载失败");
    }
  }, [selectedRunId, taskId]);

  useEffect(() => { void load(""); }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLogExport = useCallback(async () => {
    try {
      const result = await adminApi<{ exportTask: ProjectLogExportTask | null }>(`/api/admin/ppt-tasks/${encodeURIComponent(taskId)}/logs-export`);
      setLogExport(result.exportTask || null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "日志导出状态读取失败");
    }
  }, [taskId]);

  useEffect(() => { void loadLogExport(); }, [loadLogExport]);
  useEffect(() => {
    if (logExport?.status !== "queued" && logExport?.status !== "running") return;
    const timer = window.setInterval(() => void loadLogExport(), 2_000);
    return () => window.clearInterval(timer);
  }, [loadLogExport, logExport?.status]);

  const startLogExport = async () => {
    if (exportBusy) return;
    setExportBusy(true);
    setError("");
    try {
      const result = await adminApi<{ exportTask: ProjectLogExportTask }>(`/api/admin/ppt-tasks/${encodeURIComponent(taskId)}/logs-export`, { method: "POST" });
      setLogExport(result.exportTask);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "日志导出任务提交失败");
    } finally {
      setExportBusy(false);
    }
  };

  const records = useMemo(() => (data?.records || []).filter((record) => {
    if (sourceFilter !== "all" && record.source !== sourceFilter) return false;
    const query = keyword.trim().toLowerCase();
    return !query || `${record.stage} ${record.label} ${record.status}`.toLowerCase().includes(query);
  }), [data?.records, keyword, sourceFilter]);

  if (!data && !error) return <LoadingBlock/>;
  if (!data) return <><PageTitle eyebrow="任务运维" title="详细数据不可用" description={error || "任务不存在"} action={<ButtonLink variant="secondary" to={`${ADMIN_BASE_PATH}/projects/${encodeURIComponent(taskId)}`}>返回 PPT 详情</ButtonLink>}/></>;

  const observedCount = data.flow.expectedStages.filter((stage) => stage.observed).length;
  return <>
    <PageTitle
      eyebrow="任务运维 · 生成过程检查"
      title={`${data.task.title || "未命名任务"} · 详细数据`}
      description={`${data.task.id} · 数据库 #${data.task.numericId} · ${data.flow.label}`}
      action={<div className="admin-task-detail-actions"><ButtonLink variant="secondary" to={`${ADMIN_BASE_PATH}/projects/${encodeURIComponent(data.task.id)}`}>返回 PPT 详情</ButtonLink><Button type="button" variant="secondary" onClick={() => void load()}>刷新</Button><Button type="button" disabled={exportBusy || logExport?.status === "queued" || logExport?.status === "running"} onClick={() => void startLogExport()}>{exportBusy ? "提交中…" : logExport?.status === "queued" || logExport?.status === "running" ? "正在整理日志…" : "导出全部原始日志"}</Button></div>}
    />
    {error && <div className="form-error admin-task-message" role="alert">{error}</div>}
    {logExport && <div className={`admin-task-message ${logExport.status === "failed" ? "form-error" : "form-notice"}`} role="status">
      {logExport.status === "succeeded" && logExport.downloadUrl
        ? <>原始日志压缩包已整理完成{logExport.archiveBytes ? `（${formatBytes(logExport.archiveBytes)}）` : ""}，<a href={logExport.downloadUrl}>下载文件</a></>
        : logExport.status === "failed"
          ? `日志导出失败：${logExport.errorMessage || "请重新提交"}`
          : `正在后台整理全部原始日志：${logExport.progressPercent || 0}% · ${logExport.progressText || "等待专用导出 Worker 领取任务"}`}
    </div>}

    <section className="admin-diagnostic-overview">
      <div><Badge tone="brand">{data.flow.label}</Badge><h2>{observedCount} / {data.flow.expectedStages.length} 个预期环节已有记录</h2><p>先检查流程分支和缺失环节，再按时间查看每条数据库、审计及模型记录。</p></div>
      <dl>
        <div><dt>过程记录</dt><dd>{data.summary.recordCount}</dd></div>
        <div><dt>模型调用</dt><dd>{data.summary.modelCallCount}</dd></div>
        <div><dt>可展开模型载荷</dt><dd>{data.coverage.availableModelPayloads}</dd></div>
        <div><dt>审计运行</dt><dd>{data.coverage.auditRunCount}</dd></div>
      </dl>
    </section>

    {(data.flow.warnings.length > 0 || data.coverage.notes.length > 0) && <section className="admin-diagnostic-notes" aria-label="数据覆盖提示">
      {[...data.flow.warnings, ...data.coverage.notes].map((note, index) => <p key={`${index}-${note}`}><span>!</span>{note}</p>)}
    </section>}

    <section className="content-section admin-task-section">
      <div className="section-row"><div><h2>当前流程检查表</h2><p>不同模式使用不同环节；“未发现”表示现有持久化数据中没有匹配记录，不等同于该步骤一定未执行。</p></div><JsonDetails label="查看流程判定依据" value={data.flow.evidence}/></div>
      <ol className="admin-diagnostic-stage-list">
        {data.flow.expectedStages.map((stage, index) => <li key={stage.id} className={stage.observed ? "is-observed" : "is-missing"}>
          <b>{index + 1}</b><div><strong>{stage.label}</strong><p>{stage.description}</p><small>{stage.recordIds.length ? `关联 ${stage.recordIds.length} 条记录` : "未发现关联记录"}</small></div><Badge tone={stage.observed ? "success" : "warning"}>{stage.observed ? "已有数据" : "未发现"}</Badge>
        </li>)}
      </ol>
    </section>

    <section className="content-section admin-task-section">
      <div className="section-row"><div><h2>步骤时间线</h2><p>长文本已精简；有“输入/输出/请求/响应”按钮的记录可按需展开完整脱敏数据。</p></div></div>
      <div className="admin-diagnostic-toolbar">
        <label><span>数据来源</span><select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}><option value="all">全部</option><option value="audit">详细审计</option><option value="database">数据库</option><option value="project">项目状态</option></select></label>
        <label><span>阶段筛选</span><input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="输入 stage 或状态"/></label>
        {data.auditRuns.length > 0 && <label><span>审计运行</span><select value={selectedRunId} onChange={(event) => { const runId = event.target.value; setSelectedRunId(runId); void load(runId); }}>{data.auditRuns.map((run) => { const id = String(run.auditRunId || ""); return <option key={id} value={id}>{formatDate(String(run.startedAt || ""))} · {id}</option>; })}</select></label>}
      </div>
      {records.length ? <div className="admin-diagnostic-timeline">{records.map((record, index) => <DiagnosticRecordCard key={record.id} record={record} index={index}/>)}</div> : <EmptyState title="没有匹配的步骤记录" description="调整来源或阶段筛选后重试。"/>}
    </section>

    <section className="content-section admin-task-section">
      <div className="section-row"><div><h2>大模型调用</h2><p>展示模型、阶段、参数摘要、Token、耗时及提示词/响应可用性；未持久化的数据会明确标记。</p></div></div>
      {data.modelCalls.length ? <div className="admin-model-call-list">{data.modelCalls.map((call) => <article key={call.id}>
        <header><div><strong>{String(call.stage || "未知阶段")}</strong><small>{String(call.id)} · {String(call.provider || "unknown")} / {String(call.modelName || call.modelId || "unknown")}</small></div><Badge tone={statusTone(String(call.status || ""))}>{String(call.status || "unknown")}</Badge></header>
        <dl><div><dt>尝试</dt><dd>{String(call.attemptNo || 1)}</dd></div><div><dt>Token</dt><dd>{Number(call.inputTokens || 0)} / {Number(call.outputTokens || 0)}</dd></div><div><dt>耗时</dt><dd>{formatDuration(Number(call.latencyMs || 0))}</dd></div><div><dt>采纳</dt><dd>{call.accepted ? "是" : "否"}</dd></div></dl>
        <div className="admin-diagnostic-payload-actions">
          {call.requestAvailable ? <PayloadViewer taskId={data.task.id} payload={{ source: "model_call", part: "request", label: "请求 / 提示词", itemId: call.id }}/> : <span>请求/提示词未保存</span>}
          {call.responseAvailable ? <PayloadViewer taskId={data.task.id} payload={{ source: "model_call", part: "response", label: "响应", itemId: call.id }}/> : <span>响应未保存</span>}
        </div>
      </article>)}</div> : <EmptyState title="暂无模型调用记录" description="该流程可能未调用模型，或历史任务仅记录了聚合用量。"/>}
    </section>

    <section className="content-section admin-task-section">
      <div className="section-row"><div><h2>关键输入快照</h2><p>用于交叉核对入口参数、资料数量、DeckPlan 和 StyleContract，不展开原始上传文件。</p></div></div>
      <div className="admin-diagnostic-snapshots"><JsonDetails label="任务输入快照" value={data.snapshots.input}/><JsonDetails label="资料事实摘要" value={data.snapshots.sourceFacts}/><JsonDetails label="DeckPlan / StyleContract" value={data.snapshots.deck}/></div>
    </section>
  </>;
}

function DiagnosticRecordCard({ record, index }: { record: DiagnosticRecord; index: number }) {
  const { taskId = "" } = useParams();
  return <article className={`admin-diagnostic-record source-${record.source}`}>
    <i>{index + 1}</i>
    <div className="admin-diagnostic-record-body">
      <header><div><strong>{record.label}</strong><small>{record.stage} · {sourceLabel(record.source)}{record.slideIndex !== undefined ? ` · 第 ${record.slideIndex + 1} 页` : ""}{record.attemptNo ? ` · 尝试 ${record.attemptNo}` : ""}</small></div><Badge tone={statusTone(record.status)}>{record.status}</Badge></header>
      <div className="admin-diagnostic-record-time"><span>{formatDate(record.startedAt || record.finishedAt || "")}</span>{record.durationMs !== undefined && <span>{formatDuration(record.durationMs)}</span>}</div>
      {record.summary !== undefined && <pre>{jsonText(record.summary)}</pre>}
      {record.payloads && record.payloads.length > 0 && <div className="admin-diagnostic-payload-actions">{record.payloads.map((payload) => <PayloadViewer key={`${payload.source}:${payload.part}:${payload.itemId}`} taskId={taskId} payload={payload}/>)}</div>}
    </div>
  </article>;
}

function PayloadViewer({ taskId, payload }: { taskId: string; payload: PayloadRef }) {
  const [value, setValue] = useState<unknown>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (value !== undefined || busy) return;
    setBusy(true); setError("");
    try {
      const query = new URLSearchParams({ source: payload.source, part: payload.part, itemId: payload.itemId });
      if (payload.runId) query.set("runId", payload.runId);
      if (payload.kind) query.set("kind", payload.kind);
      const result = await adminApi<{ payload: unknown }>(`/api/admin/ppt-tasks/${encodeURIComponent(taskId)}/diagnostics/payload?${query}`);
      setValue(result.payload);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "载荷读取失败");
    } finally { setBusy(false); }
  };
  return <div className="admin-diagnostic-payload"><Button type="button" variant="secondary" onClick={() => void toggle()}>{open ? `收起${payload.label}` : `查看${payload.label}`}</Button>{open && <div className="admin-diagnostic-payload-content">{busy ? <LoadingBlock/> : error ? <p className="form-error">{error}</p> : <pre>{jsonText(value)}</pre>}</div>}</div>;
}

function JsonDetails({ label, value }: { label: string; value: unknown }) {
  return <details className="admin-json-details"><summary>{label}</summary><pre>{jsonText(value)}</pre></details>;
}

function jsonText(value: unknown): string {
  if (value === undefined) return "暂无数据";
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}

function sourceLabel(source: DiagnosticRecord["source"]): string { return ({ project: "项目", database: "数据库", audit: "详细审计" } as const)[source]; }
function statusTone(status: string): "neutral" | "brand" | "success" | "warning" | "danger" {
  if (["succeeded", "saved", "completed", "success"].includes(status)) return "success";
  if (["failed", "rejected", "error"].includes(status)) return "danger";
  if (["started", "running", "planning", "rendering", "queued"].includes(status)) return "brand";
  if (["partial", "cancelled", "interrupted"].includes(status)) return "warning";
  return "neutral";
}
function formatDate(value: string): string { if (!value) return "–"; const date = new Date(value); return Number.isNaN(date.getTime()) ? value : date.toLocaleString("zh-CN", { hour12: false }); }
function formatDuration(value: number): string { if (!value) return "0 ms"; return value >= 60_000 ? `${(value / 60_000).toFixed(1)} 分钟` : value >= 1_000 ? `${(value / 1_000).toFixed(1)} 秒` : `${value} ms`; }
function formatBytes(value: number): string { return value >= 1024 ** 3 ? `${(value / 1024 ** 3).toFixed(2)} GiB` : value >= 1024 ** 2 ? `${(value / 1024 ** 2).toFixed(2)} MiB` : `${Math.max(0, Math.round(value / 1024))} KiB`; }
